import "./Game.css"
import React, { useRef, useState, useEffect } from 'react';

import { ALPHABET, COMPETITION_THEME_COUNTRIES, COMPETITION_THEME_POKEMON, COMPETITION_THEME_SCRABBLE, KEYBOARD_INPUT_KEYS } from "../utils/constants";

import { GoQuestion } from "react-icons/go";

import { guessWordCompetition, getWordDefinitions } from "../https_handler/apiHandler";

import CountDownTimer from "./CountDownTimer";

import WordDefinitionModal from "./WordDefinitionModal";
import "bootstrap/dist/css/bootstrap.min.css";

import { isMobile } from 'react-device-detect';

const Game = ({ user, activeCompetition, showErrorModal, handleCorrectGuess, dateToNextWord, reloadNewComp, showTimerLoading, setShowScoreBoard, setRemainingGuesses, remainingGuesses, guesses, setGuesses }) => {
    const [word, setWord] = React.useState("");
    const [isSendingGuess, setIsSendingGuess] = React.useState(false);
    const [showWordDefinitionModal, setShowWordDefinitionModal] = React.useState(false);
    const [wordDefinitions, setWordDefinitions] = React.useState(false);
    const [letterColor, setLetterColor] = React.useState();
    const [correctGuess, setCorrectGuess] = React.useState();
    const [loadingDefinition, setLoadingDefinition] = React.useState(false);
    const [finishedAnimation, setFinishedAnimation] = React.useState(true);
    
    // Load dictionaries dynamically
    const [globalDict, setGlobalDict] = useState(null);
    const [globalDictPokemon, setGlobalDictPokemon] = useState(null);
    const [globalDictCountries, setGlobalDictCountries] = useState(null);

    useEffect(() => {
        // Load all dictionaries in parallel
        Promise.all([
            fetch('/globalDict.json').then(r => r.json()),
            fetch('/globalDict_pokemon.json').then(r => r.json()),
            fetch('/globalDict_countries.json').then(r => r.json())
        ]).then(([dict, pokemon, countries]) => {
            setGlobalDict(dict);
            setGlobalDictPokemon(pokemon);
            setGlobalDictCountries(countries);
        }).catch(err => console.error('Failed to load dictionaries:', err));
    }, []);

    const inputContainer = useRef(null);
    const letterInputContainerRef = useRef(null);

    const handleKeyInput = (key) => {
        if (word.length < activeCompetition.compInfo.wordLength) {
            setLetterColor("black");
            setWord(`${word}${key}`);
        }
    }

    const handleDeleteKey = () => {
        setWord(word.slice(0, word.length - 1))
    }

    const handleEnterKey = () => {
        if (word.length === activeCompetition.compInfo.wordLength) {

            if (isValidWord(word)) {

                if (guesses.length >= activeCompetition.compInfo.maxGuesses) return;

                if (!activeCompetition) showErrorModal("Select a competition first");

                if (activeCompetition) makeGuessComp();

            } else setLetterColor("red");
        } else {
            setLetterColor("red");
        }
    }

    const shouldRenderTimerToNextWord = () => {
        if (!activeCompetition) return false;

        if (getGuessCount() === activeCompetition.compInfo.maxGuesses) return true;

        // Check local guesses first (more recent)
        if (guesses.length > 0 && guesses[guesses.length - 1].result.isCorrect) return true;

        // Check server guesses as fallback
        if (activeCompetition && activeCompetition.user && activeCompetition.user.currentGuesses.length > 0)
            if (activeCompetition.user.currentGuesses[activeCompetition.user.currentGuesses.length - 1].isCorrect) return true;

        return false;
    }

    const wasWordGuessedCorrectly = () => {
        if (!activeCompetition) return false;
        
        // Check local guesses first (more recent)
        if (guesses.length > 0 && guesses[guesses.length - 1].result.isCorrect) return true;
        
        // Check server guesses as fallback
        if (activeCompetition.user && activeCompetition.user.currentGuesses.length > 0) {
            return activeCompetition.user.currentGuesses[activeCompetition.user.currentGuesses.length - 1].isCorrect;
        }
        
        return false;
    }

    const makeGuessComp = () => {
        if (activeCompetition.compInfo.finished === true) return showErrorModal("Sorry this competition is no longer running.");
        if (activeCompetition.compInfo.started === false) return showErrorModal("Sorry this competition has not started yet please wait until competitions starts.");

        if (isSendingGuess) return;
        setIsSendingGuess(true);
        setFinishedAnimation(false);

        guessWordCompetition(user, word, activeCompetition.compInfo.compId).then((result) => {

            const { error, isCorrect } = result;
            if (error) {
                showErrorModal(error);
                return;
            }


            setIsSendingGuess(false);
            setGuesses([...guesses, { word: word, result, compId: activeCompetition.compInfo.compId }]);
            setRemainingGuesses(remainingGuesses - 1)
            setWord("");

            if (isCorrect) {
                setTimeout(() => {
                    handleCorrectGuess().then(() => {
                        setCorrectGuess(word)
                        setGuesses([]);
                        setFinishedAnimation(true);
                    });
                }, (activeCompetition.compInfo.wordLength * 1000));
            }

        });

    }

    const handleKeyCapture = (event) => {
        if (guesses.length === activeCompetition.compInfo.maxGuesses) return;

        const key = typeof event === "string" ? event : event.key.toLowerCase();

        if (key === KEYBOARD_INPUT_KEYS.ENTER) {
            event.preventDefault();
            if (word.length === activeCompetition.compInfo.wordLength)
                handleEnterKey();
            // else handleInputContainerClick();
        }

        // For mobile, most input will be handled by onChange
        // This mainly handles special keys for desktop
        if (key === KEYBOARD_INPUT_KEYS.BACKSPACE) {
            // Let the input field handle backspace naturally
            return;
        }

        if (ALPHABET.includes(key)) {
            // Let the onChange handler deal with letter input
            return;
        }
    }

    const renderGuesses = () => {
        if (guesses.length > 0) {
            if (guesses[0].compId !== activeCompetition.compInfo.compId)
                setGuesses([])
        }

        let combinedGuess = guesses;
        if (activeCompetition && activeCompetition.user)
            combinedGuess = [...activeCompetition.user.currentGuesses.map(guess => ({ word: guess.guess, result: guess })), ...guesses];

        return combinedGuess.map((guess, index) => {
            return (
                <div key={`guess_${index}`} className="guess-word-container">
                    {renderGuess(guess, index, index === combinedGuess.length - 1)}
                    {guess.result.isCorrect && finishedAnimation && activeCompetition.compInfo.compType === COMPETITION_THEME_SCRABBLE && (
                        <button
                            onClick={handleMouseEnterInfoModal}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--secondary-blue)',
                                cursor: 'pointer',
                                marginLeft: 'var(--space-sm)',
                                fontSize: '1.25rem'
                            }}
                            title="View definition"
                        >
                            <GoQuestion size={20} />
                        </button>
                    )}
                </div>
            );
        });
    }

    const shouldRenderGuessKey = () => {
        if (guesses.length > 0 || (activeCompetition && activeCompetition && activeCompetition.user.currentGuesses.length > 0)) return true;

        return false;
    }

    const renderGuess = (guess, index, isLatestGuess) => {
        const letters = guess.word.split("");

        return letters.map((guessLetter, letterIndex) => {
            const colorClass = getGuessLetterClass(guess, letterIndex);

            return (
                <div
                    key={`guess_${index}_${letterIndex}`}
                    className={`guess-letter ${colorClass}`}
                >
                    {guessLetter.toUpperCase()}
                </div>
            );
        });
    }

    const getGuessLetterClass = (guess, letterIndex) => {
        if (guess.result.occurrences[letterIndex] === 2) return "correct";
        if (guess.result.occurrences[letterIndex] === 1) return "present";
        return "absent";
    }

    // Function to get the status of a letter at a specific position based on all previous guesses
    const getLetterStatusAtPosition = (letter, position) => {
        let combinedGuesses = [];

        // Combine current session guesses with previous guesses from server
        if (activeCompetition && activeCompetition.user) {
            combinedGuesses = [...activeCompetition.user.currentGuesses, ...guesses];
        } else {
            combinedGuesses = guesses;
        }

        // Check all guesses for this letter at this specific position
        for (const guess of combinedGuesses) {
            const guessWord = guess.word || guess.guess;
            const result = guess.result || guess;

            if (guessWord && guessWord.length > position) {
                const letterAtPosition = guessWord.toLowerCase()[position];

                // If this exact letter was guessed at this exact position
                if (letterAtPosition === letter.toLowerCase() &&
                    result.occurrences &&
                    result.occurrences[position] !== undefined) {

                    if (result.occurrences[position] === 2) return 'correct'; // Green - correct position
                    if (result.occurrences[position] === 1) return 'present'; // Yellow - wrong position  
                    if (result.occurrences[position] === 0) return 'absent';  // Gray - not in word
                }
            }
        }

        // If letter wasn't tried at this specific position, check what we know about it from other positions
        let letterFoundInWord = false;
        let letterIsAbsent = false;

        for (const guess of combinedGuesses) {
            const guessWord = guess.word || guess.guess;
            const result = guess.result || guess;

            if (guessWord) {
                for (let i = 0; i < guessWord.length; i++) {
                    if (guessWord.toLowerCase()[i] === letter.toLowerCase() &&
                        result.occurrences &&
                        result.occurrences[i] !== undefined) {
                        
                        // If the letter was found correct (2) or present (1) anywhere, it's in the word
                        if (result.occurrences[i] === 2 || result.occurrences[i] === 1) {
                            letterFoundInWord = true;
                        }
                        // If the letter was found to be absent (0) anywhere, it's not in the word at all
                        if (result.occurrences[i] === 0) {
                            letterIsAbsent = true;
                        }
                    }
                }
            }
        }

        // If we know the letter is in the word (found as correct or present elsewhere)
        // but hasn't been tried at this position, show it as present (yellow)
        if (letterFoundInWord) return 'present';
        
        // If we know the letter is not in the word at all, show it as absent
        if (letterIsAbsent) return 'absent';

        // If letter hasn't been tried anywhere, return unused
        return 'unused';
    }

    // Function to get general status of a letter (fallback)
    const getLetterStatusGeneral = (letter) => {
        let combinedGuesses = [];

        if (activeCompetition && activeCompetition.user) {
            combinedGuesses = [...activeCompetition.user.currentGuesses, ...guesses];
        } else {
            combinedGuesses = guesses;
        }

        let bestStatus = 'unused';

        // Check all guesses for this letter anywhere in the word
        for (const guess of combinedGuesses) {
            const guessWord = guess.word || guess.guess;
            const result = guess.result || guess;

            if (guessWord) {
                for (let i = 0; i < guessWord.length; i++) {
                    if (guessWord.toLowerCase()[i] === letter.toLowerCase() &&
                        result.occurrences &&
                        result.occurrences[i] !== undefined) {

                        if (result.occurrences[i] === 2) return 'correct'; // Best possible status
                        if (result.occurrences[i] === 1 && bestStatus !== 'correct') bestStatus = 'present';
                        if (result.occurrences[i] === 0 && bestStatus === 'unused') bestStatus = 'absent';
                    }
                }
            }
        }

        return bestStatus;
    }

    // Function to get the color for a letter in the current input at a specific position
    const getInputLetterColor = (letter, position) => {
        if (letterColor === 'red') return 'var(--error)';

        const status = getLetterStatusAtPosition(letter, position);
        switch (status) {
            case 'correct':
                return 'var(--success)'; // Green - correct position
            case 'present':
                return 'var(--warning)'; // Yellow/Orange - wrong position
            case 'absent':
                return 'var(--error)'; // Red - not in word
            default:
                return 'var(--gray-800)'; // Default black - unused
        }
    }

    const renderLetterInputs = () => {
        if (activeCompetition && activeCompetition.user) {
            return [...new Array(activeCompetition.compInfo.wordLength)].map((value, index) => {
                const letter = word.split("")[index] || '';
                const isCurrentPosition = word.length === index;
                const hasLetter = letter !== '';
                const hasError = letterColor === 'red';

                return (
                    <input
                        key={`letter_${index}`}
                        type="text"
                        className={`letter-input ${hasLetter ? 'filled' : ''} ${hasError ? 'error' : ''} ${isCurrentPosition ? 'current' : ''}`}
                        value={letter}
                        maxLength={1}
                        readOnly
                        style={{
                            color: hasLetter ? getInputLetterColor(letter, index) : 'var(--gray-800)'
                        }}
                    />
                );
            });
        }
        return null;
    }

    const getGuessLetterColour = (guess, letterIndex) => {
        if (guess.result.occurrences[letterIndex] === 2) return "green";

        if (guess.result.occurrences[letterIndex] === 1) return "yellow"

        return "red";
    }

    const getGuessCount = () => {
        let guessCount = guesses.length;

        if (activeCompetition && activeCompetition.user)
            guessCount += activeCompetition.user.guessCountForIteration;

        if (guessCount < 0) guessCount *= -1;

        return guessCount;
    }

    const handleMouseEnterInfoModal = () => {
        if (loadingDefinition) return;

        setLoadingDefinition(true);

        let guess = correctGuess;
        const currentGuess = getCorrectGuessWord();

        if (!correctGuess || guess !== currentGuess) {
            guess = currentGuess;
            setCorrectGuess(guess)
        }

        if (wordDefinitions[guess]) {
            setLoadingDefinition(false);
            setShowWordDefinitionModal(true);
        }
        else {
            getWordDefinitions(guess, user).then(response => {
                setLoadingDefinition(false);
                setWordDefinitions({ ...wordDefinitions, [guess]: response });
                setShowWordDefinitionModal(true);
            });
        }
    }

    const handleMouseLeaveInfoModal = async () => {
        setShowWordDefinitionModal(false);
    }

    const getCorrectGuessWord = () => {
        if (activeCompetition && activeCompetition.user && activeCompetition.user.currentGuesses.length > 0)
            if (activeCompetition.user.currentGuesses[activeCompetition.user.currentGuesses.length - 1].isCorrect)
                return activeCompetition.user.currentGuesses[activeCompetition.user.currentGuesses.length - 1].guess;
    }

    const handleInputContainerClick = () => {
        // Focus the input container to enable keyboard input on mobile
        if (inputContainer.current) {
            inputContainer.current.focus();

            // On mobile devices, scroll the input into view when keyboard appears
            if (isMobile && letterInputContainerRef.current) {
                // Add a small delay to allow the keyboard to appear
                setTimeout(() => {
                    const element = letterInputContainerRef.current;
                    const rect = element.getBoundingClientRect();
                    const viewportHeight = window.innerHeight;

                    // Calculate the position to center the input in the visible area
                    // Account for keyboard taking up roughly 40% of screen height
                    const availableHeight = viewportHeight * 0.6;
                    const targetPosition = window.scrollY + rect.top - (availableHeight / 2) + (rect.height / 2);

                    window.scrollTo({
                        top: Math.max(0, targetPosition),
                        behavior: 'smooth'
                    });
                }, 300); // 300ms delay to allow keyboard animation
            }
        }
    }

    const isValidWord = (word) => {
        const { compType, difficulty } = activeCompetition.compInfo;

        if (!difficulty.restrictGuesses || compType === COMPETITION_THEME_SCRABBLE) if (globalDict[word]) return true;

        if (compType === COMPETITION_THEME_COUNTRIES) {
            if (globalDictCountries.words.includes(word)) return true;
        }
        else if (compType === COMPETITION_THEME_POKEMON) if (globalDictPokemon.words.includes(word)) {
            return true
        };
    }

    return (
        <div className="game-container">
            {/* Game Instructions */}
            {!activeCompetition && (
                <div className="no-competition">
                    <h3>🎯 Select a Competition</h3>
                    <p>Choose a competition from the dropdown above to start playing!</p>
                </div>
            )}

            {activeCompetition && (
                <>
                    {/* Game Header */}
                    {activeCompetition.compInfo.hint && (

                        <div className="game-header">
                            <div>
                                <div className="game-instructions">
                                    <h3>💡 Hint</h3>
                                    <p>{activeCompetition.compInfo.hint}</p>
                                </div>
                            </div>
                        </div>
                    )}


                    {/* Word Input Area */}
                    {shouldRenderTimerToNextWord() ? (
                        <div className={`timer-container ${wasWordGuessedCorrectly() ? 'success' : 'try-again'}`}>
                            <h3 className="timer-title">
                                {wasWordGuessedCorrectly() ? '🎉 Great Job! Next word in:' : '😔 Better luck next time! Next word in:'}
                            </h3>
                            <CountDownTimer
                                startDate={dateToNextWord}
                                text={""}
                                reloadNewComp={reloadNewComp}
                                showTimerLoading={showTimerLoading}
                            />
                        </div>
                    ) : null}

                    <>
                        {/* Guess History */}
                        {(guesses.length > 0 || (activeCompetition.user && activeCompetition.user.currentGuesses.length > 0)) && (
                            <div className="guess-history">
                                <div className="guess-history-header">
                                    📝 Your Guesses
                                </div>
                                {renderGuesses()}

                                {/* Color Key */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 'var(--space-lg)',
                                    marginTop: 'var(--space-lg)',
                                    flexWrap: 'wrap',
                                    fontSize: window.innerWidth < 480 ? '0.75rem' : '0.875rem'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', minWidth: 'fit-content' }}>
                                        <div style={{ width: '16px', height: '16px', backgroundColor: 'var(--success)', borderRadius: '4px' }}></div>
                                        <span style={{ color: 'var(--gray-600)' }}>Correct</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', minWidth: 'fit-content' }}>
                                        <div style={{ width: '16px', height: '16px', backgroundColor: 'var(--warning)', borderRadius: '4px' }}></div>
                                        <span style={{ color: 'var(--gray-600)' }}>Wrong position</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', minWidth: 'fit-content' }}>
                                        <div style={{ width: '16px', height: '16px', backgroundColor: 'var(--gray-400)', borderRadius: '4px' }}></div>
                                        <span style={{ color: 'var(--gray-600)' }}>Not in word</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {shouldRenderTimerToNextWord() === false ? (
                            <>
                                <div
                                    ref={letterInputContainerRef}
                                    className="letter-input-container"
                                    onClick={handleInputContainerClick}
                                    role="textbox"
                                    aria-label="Type your guess here"
                                    data-mobile-hint={isMobile ? "Tap here to type" : ""}
                                >
                                    {renderLetterInputs()}
                                    {/* Hidden input for mobile keyboard */}
                                    <input
                                        ref={inputContainer}
                                        type="text"
                                        value={word}
                                        onChange={(e) => {
                                            const newValue = e.target.value.toLowerCase().replace(/[^a-z]/g, '');
                                            if (newValue.length <= activeCompetition.compInfo.wordLength) {
                                                setWord(newValue);
                                                setLetterColor("black");
                                            }
                                        }}
                                        onKeyUp={handleKeyCapture}
                                        style={{
                                            position: 'absolute',
                                            // left: '-9999px',
                                            opacity: 0,
                                            pointerEvents: 'none',
                                            caretColor: 'transparent'
                                        }}
                                        maxLength={activeCompetition.compInfo.wordLength}
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />

                                </div>

                                <div className="guess-button-container">
                                    <button
                                        className="guess-button"
                                        onClick={handleEnterKey}
                                        disabled={word.length !== activeCompetition.compInfo.wordLength || isSendingGuess}
                                    >
                                        {isSendingGuess ? 'Submitting...' : 'Submit Guess'} 🎯
                                    </button>
                                </div>
                            </>) : null}
                    </>
                </>
            )}

            {/* Word Definition Modal */}
            <WordDefinitionModal
                show={showWordDefinitionModal}
                handleClose={handleMouseLeaveInfoModal}
                guess={correctGuess}
                wordDefinitions={wordDefinitions}
            />
        </div>
    )
}

export default Game;

const SingleWordInput = ({ input, letterColor, keyName }) => {
    return (
        <div className="letter-input"
            style={{ color: letterColor }}
            key={`container_${keyName}`}
        >
            <p key={`letter_${keyName}`}>{input}</p>
        </div>
    )
}

const SingleWordInputFading = ({ input, letterColor, keyName, index }) => {
    return (
        <div style={{
            minWidth: "10%",
            opacity: 0,
            fontSize: "calc(10px + 2vmin)",
            animation: `fadeIn 0.1s`,
            animationDelay: `${index / 2}s`,
            animationFillMode: "forwards",
            color: letterColor
        }}
            key={`container_${keyName}`}
        >
            <p key={`letter_${keyName}`}>{input}</p>
        </div>
    )
}

const SingleWordInputSpinning = ({ input, letterColor, keyName, index }) => {
    return (
        <div className="letter-input"
            style={{
                minWidth: "10%",
                fontSize: "calc(10px + 2vmin)",
                animation: `spinLetter ${(index + 5) / 2}s, colourTransform-${letterColor} 200ms ease-in-out ${(index + 5) / 2}s`,
                color: "black",
                animationFillMode: "forwards",
            }}
            key={`container_${keyName}`}
        >
            <p key={`letter_${keyName}`}>{input}</p>
        </div>
    )
}